import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addSupportTicketMessage,
  closeSupportTicket,
  createSupportTicket,
  continueSupportTicket,
  fetchSupportTicket,
  fetchSupportTickets,
  reopenSupportTicket,
} from "../api/supportApi";
import { AUTH_EVENT, getActiveSession, SESSION_STORAGE_KEY } from "../utils/nightStayAccount";

const QueryContext = createContext({
  queries: [],
  queryCount: 0,
  loading: false,
  syncing: false,
  error: null,
  isAuthenticated: false,
  raiseQuery: async () => null,
  updateQueryStatus: () => null,
  addQueryNote: async () => null,
  closeQuery: async () => null,
  continueQuery: async () => null,
  reopenQuery: async () => null,
  refreshQueries: async () => null,
  refreshQuery: async () => null,
  listQueriesPage: async () => null,
});

const sessionKey = () => { const session = getActiveSession(); return String(session?.id || session?.userId || session?.email || session?.phone || ""); };
const RECENT_QUERY_LIMIT = 40;
const PORTAL_STATUS_QUERY_MAP = {
  all: "",
  open: "new,acknowledged,in_progress,reopened",
  waiting: "pending_customer",
  resolved: "resolved",
  closed: "closed,discarded",
};

const sortRecentQueries = (items = []) =>
  [...items].sort(
    (left, right) =>
      new Date(right?.updatedAt || right?.createdAt || 0).getTime() -
      new Date(left?.updatedAt || left?.createdAt || 0).getTime()
  );

const normalizeActor = (actor = {}) => ({
  id: actor?.id || "",
  name: actor?.name || "Support desk",
  email: actor?.email || "",
  phone: actor?.phone || "",
  roles: Array.isArray(actor?.roles) ? actor.roles : [],
});

const normalizeAttachment = (item, index = 0) => {
  if (!item) return null;
  return {
    id: item.id || item.assetId || `attachment-${index + 1}`,
    name: item.title || item.name || `attachment-${index + 1}`,
    type: item.mimeType || item.type || "application/octet-stream",
    size: item.size || 0,
    url: item.url || item.dataUrl || null,
    dataUrl: item.url || item.dataUrl || null,
    preview: item.thumbnailUrl || item.preview || null,
  };
};

const normalizeBookingAttachment = (item) => {
  if (!item) return null;
  const referenceCode = String(item.referenceCode || "").trim();
  if (!referenceCode) return null;
  return {
    referenceCode,
    bookingType: String(item.bookingType || "").trim(),
    destinationName: String(item.destinationName || "").trim(),
    campName: String(item.campName || "").trim(),
    inventoryName: String(item.inventoryName || "").trim(),
    travelDate: item.travelDate || null,
    status: String(item.status || "").trim(),
  };
};

const normalizeReceiptAttachment = (item) => {
  if (!item) return null;
  const referenceCode = String(item.referenceCode || "").trim();
  if (!referenceCode) return null;
  return {
    referenceCode,
    bookingType: String(item.bookingType || "").trim(),
    receiptNumber: String(item.receiptNumber || "").trim(),
    receiptStatus: String(item.receiptStatus || "").trim(),
    receiptUrl: String(item.receiptUrl || "").trim(),
    paymentReceiptUrl: String(item.paymentReceiptUrl || "").trim(),
    generatedAt: item.generatedAt || null,
  };
};

const normalizeTimeline = (entries = []) =>
  (Array.isArray(entries) ? entries : []).map((entry, index) => {
    const actor = normalizeActor(entry?.actor);
    return {
      id: entry?.id || `${entry?.createdAt || "entry"}-${index}`,
      type: entry?.type || "note",
      message: entry?.message || "",
      status: entry?.status || "new",
      actor,
      actorName: actor.name,
      createdAt: entry?.createdAt || null,
      visibility: entry?.visibility || "public",
      links: Array.isArray(entry?.links) ? entry.links : [],
      attachments: Array.isArray(entry?.attachments)
        ? entry.attachments.map((item, attachmentIndex) => normalizeAttachment(item, attachmentIndex)).filter(Boolean)
        : [],
      bookingAttachments: Array.isArray(entry?.bookingAttachments)
        ? entry.bookingAttachments.map((item) => normalizeBookingAttachment(item)).filter(Boolean)
        : [],
      receiptAttachments: Array.isArray(entry?.receiptAttachments)
        ? entry.receiptAttachments.map((item) => normalizeReceiptAttachment(item)).filter(Boolean)
        : [],
    };
  });

export const normalizeTicket = (ticket) => ({
  ...ticket,
  subject: ticket?.title || "Support ticket",
  bookingId: ticket?.bookingContext?.referenceCode || null,
  bookingReference: ticket?.bookingContext?.referenceCode || null,
  contact: {
    name: ticket?.requester?.name || null,
    email: ticket?.requester?.email || null,
    phone: ticket?.requester?.phone || null,
  },
  attachments: Array.isArray(ticket?.attachments)
    ? ticket.attachments.map((item, index) => normalizeAttachment(item, index)).filter(Boolean)
    : [],
  timeline: normalizeTimeline(ticket?.activityLog),
});

const resolvePortalStatusQuery = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return PORTAL_STATUS_QUERY_MAP[normalized] ?? normalized;
};

export const QueryProvider = ({ children, enabled = true }) => {
  const [queries, setQueries] = useState([]);
  const [queryCount, setQueryCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getActiveSession()));
  const accountRef = useRef(sessionKey());

  const upsertQuery = useCallback((ticket, owner = sessionKey()) => {
    if (owner !== sessionKey()) throw new Error("Account changed. Open this conversation from the current account.");
    if (!ticket?.id) throw new Error("The server did not confirm the saved conversation. Please retry.");
    const next = normalizeTicket(ticket);
    setQueries((prev) => {
      const items = Array.isArray(prev) ? prev : [];
      const index = items.findIndex((item) => item.id === next.id);
      if (index === -1) {
        return sortRecentQueries([next, ...items]).slice(0, RECENT_QUERY_LIMIT);
      }
      const updated = [...items];
      updated[index] = next;
      return sortRecentQueries(updated).slice(0, RECENT_QUERY_LIMIT);
    });
    return next;
  }, []);

  const refreshQueries = useCallback(async ({ silent = false } = {}) => {
    const session = getActiveSession();
    const owner = sessionKey();
    if (owner !== accountRef.current) {
      accountRef.current = owner; setQueries([]); setQueryCount(0); setError(null);
    }
    const hasSession = Boolean(session);
    setIsAuthenticated(hasSession);
    if (!hasSession) {
      setQueries([]);
      setQueryCount(0);
      setError(null);
      return [];
    }

    const isSilentRefresh = Boolean(silent);
    if (isSilentRefresh) {
      setSyncing(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await fetchSupportTickets({
        limit: RECENT_QUERY_LIMIT,
        sort: "updated",
      });
      const items = Array.isArray(response?.data) ? response.data.map(normalizeTicket) : [];
      if (owner !== sessionKey()) return [];
      setQueries(sortRecentQueries(items).slice(0, RECENT_QUERY_LIMIT));
      setQueryCount(Number(response?.total) || items.length);
      return items;
    } catch (requestError) {
      if (owner === sessionKey() && !isSilentRefresh) {
        setError(requestError?.message || "Unable to load support tickets.");
      }
      return [];
    } finally {
      if (isSilentRefresh) {
        setSyncing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const refreshQuery = useCallback(async (queryId, { silent = true, throwOnError = false } = {}) => {
    const owner = sessionKey();
    if (!getActiveSession() || !queryId) return null;
    if (silent) {
      setSyncing(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await fetchSupportTicket(queryId);
      return upsertQuery(response?.data, owner);
    } catch (requestError) {
      if (throwOnError) throw requestError;
      if (!silent) {
        setError(requestError?.message || "Unable to refresh the support ticket.");
      }
      return null;
    } finally {
      if (silent) {
        setSyncing(false);
      } else {
        setLoading(false);
      }
    }
  }, [upsertQuery]);

  const listQueriesPage = useCallback(
    async ({
      page = 1,
      pageSize = 8,
      search = "",
      status = "",
      bookingId = "",
      sort = "updated",
    } = {}) => {
      const owner = sessionKey();
      if (!getActiveSession()) {
        return {
          data: [],
          total: 0,
          page: 1,
          pageSize,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }
      const safePage = Math.max(Number(page) || 1, 1);
      const safePageSize = Math.max(Number(pageSize) || 8, 1);
      const resolvedStatus = resolvePortalStatusQuery(status);
      const response = await fetchSupportTickets({
        limit: safePageSize,
        skip: (safePage - 1) * safePageSize,
        search: String(search || "").trim(),
        status: resolvedStatus,
        bookingId: String(bookingId || "").trim(),
        sort,
      });
      const items = Array.isArray(response?.data) ? response.data.map(normalizeTicket) : [];
      if (owner === sessionKey() && !search && !resolvedStatus && !bookingId) {
        setQueryCount(Number(response?.total) || items.length);
      }
      return {
        data: items,
        total: Number(response?.total) || items.length,
        page: Math.max(Number(response?.page) || safePage, 1),
        pageSize: Math.max(Number(response?.pageSize || response?.limit) || safePageSize, 1),
        totalPages: Math.max(Number(response?.totalPages) || 1, 1),
        hasNextPage: Boolean(response?.hasNextPage),
        hasPreviousPage: Boolean(response?.hasPreviousPage),
      };
    },
    []
  );

  useEffect(() => {
    if (!enabled) return undefined;
    refreshQueries();
    return undefined;
  }, [enabled, refreshQueries]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return () => {};
    const handleAuthChange = () => {
      refreshQueries();
    };
    window.addEventListener(AUTH_EVENT, handleAuthChange);
    const handleStorage = (event) => { if (!event.key || event.key === SESSION_STORAGE_KEY) handleAuthChange(); };
    window.addEventListener("storage", handleStorage);
    return () => { window.removeEventListener(AUTH_EVENT, handleAuthChange); window.removeEventListener("storage", handleStorage); };
  }, [enabled, refreshQueries]);

  const raiseQuery = useCallback(
    async (payload) => {
      const owner = sessionKey();
      if (!getActiveSession()) {
        throw new Error("Sign in to raise a support ticket.");
      }
      const supportPayload = {
        ...(payload?.clientRequestId ? { clientRequestId: payload.clientRequestId } : {}),
        ...(payload?.relatedTicketId ? { relatedTicketId: payload.relatedTicketId } : {}),
        subject: payload?.subject,
        description: payload?.description,
        category: payload?.category,
        ...(payload?.subcategory ? { subcategory: payload.subcategory } : {}),
        priority: payload?.priority,
        ...(payload?.bookingId ? { bookingId: payload.bookingId } : {}),
        sourceContext: payload?.sourceContext,
        ...(payload?.faqKey ? { faqKey: payload.faqKey } : {}),
        context: payload?.context,
        ...(payload?.contact &&
        (payload.contact.name || payload.contact.email || payload.contact.phone)
          ? {
              contact: {
                ...(payload.contact.name ? { name: payload.contact.name } : {}),
                ...(payload.contact.email ? { email: payload.contact.email } : {}),
                ...(payload.contact.phone ? { phone: payload.contact.phone } : {}),
              },
            }
          : {}),
        attachments: Array.isArray(payload?.attachments)
          ? payload.attachments.map((item) => ({
              title: item?.name || item?.title || "",
              mimeType: item?.mimeType || item?.type || "",
              size: item?.size || 0,
              url: item?.url || item?.dataUrl || "",
              key: item?.key || "",
              thumbnailUrl: item?.thumbnailUrl || item?.preview || "",
            }))
          : [],
      };
      const response = await createSupportTicket(supportPayload);
      const nextTicket = upsertQuery(response?.data, owner);
      if (!response?.replayed) setQueryCount((current) => current + 1);
      return nextTicket;
    },
    [upsertQuery]
  );

  const addQueryNote = useCallback(async (queryId, payload) => {
    const owner = sessionKey();
    const message =
      typeof payload === "string" ? payload : String(payload?.message || "").trim();
    if (!queryId || !message) return null;
    const response = await addSupportTicketMessage(queryId, {
      ...(payload?.clientRequestId ? { clientRequestId: payload.clientRequestId } : {}),
      message,
      attachments: Array.isArray(payload?.attachments)
        ? payload.attachments.map((item) => ({
            title: item?.name || item?.title || "",
            mimeType: item?.mimeType || item?.type || "",
            size: item?.size || 0,
            url: item?.url || item?.dataUrl || "",
            key: item?.key || "",
            thumbnailUrl: item?.thumbnailUrl || item?.preview || "",
          }))
        : [],
    });
    return upsertQuery(response?.data, owner);
  }, [upsertQuery]);

  const closeQuery = useCallback(async (queryId, reason, clientRequestId) => {
    const owner = sessionKey();
    if (!queryId) return null;
    const response = await closeSupportTicket(queryId, { reason: String(reason || "").trim(), ...(clientRequestId ? { clientRequestId } : {}) });
    return upsertQuery(response?.data, owner);
  }, [upsertQuery]);

  const continueQuery = useCallback(async (queryId, clientRequestId) => {
    const owner = sessionKey();
    const response = await continueSupportTicket(queryId, { clientRequestId });
    return upsertQuery(response?.data, owner);
  }, [upsertQuery]);

  const reopenQuery = useCallback(async (queryId, reason) => {
    const owner = sessionKey();
    if (!queryId) return null;
    const response = await reopenSupportTicket(queryId, { reason: String(reason || "").trim() });
    return upsertQuery(response?.data, owner);
  }, [upsertQuery]);

  const value = useMemo(
    () => ({
      queries,
      queryCount,
      loading,
      syncing,
      error,
      isAuthenticated,
      raiseQuery,
      updateQueryStatus: () => null,
      addQueryNote,
      closeQuery,
      continueQuery,
      reopenQuery,
      refreshQueries,
      refreshQuery,
      listQueriesPage,
    }),
    [
      queries,
      queryCount,
      loading,
      syncing,
      error,
      isAuthenticated,
      raiseQuery,
      addQueryNote,
      closeQuery,
      continueQuery,
      reopenQuery,
      refreshQueries,
      refreshQuery,
      listQueriesPage,
    ]
  );

  return <QueryContext.Provider value={value}>{children}</QueryContext.Provider>;
};

export const useQueryDesk = () => useContext(QueryContext);
